import { SIDE_PANEL_WIDTH } from '@/constants/side-panel'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Gift, X } from 'lucide-react'
import {
    AppPanel,
    AppPanelBody,
    AppPanelFooter,
    AppPanelHeader,
} from '@vnetwork/datacentral-ui/layout'
import {
    Badge,
    Button,
    Disclosure,
    IconButton,
    SegmentedControl,
    Text,
    type BadgeTone,
} from '@vnetwork/datacentral-ui/primitives'
import { EmptyState } from '@vnetwork/datacentral-ui/data'
import { formatDateWithFormatter } from '@/utils/function'
import { sanitizeHtml } from '@/utils/sanitize-html'
import { paths } from '@/utils/path'
import type { ReleaseNote } from '@/types/releaseNotes'
import { useReleaseNotes } from './use-release-notes'

const CATEGORY_TONE: Record<ReleaseNote['category'], BadgeTone> = {
    feature: 'success',
    bugfix: 'danger',
    improvement: 'primary',
    security: 'warning',
}

/** Release notes docked beside the page (DashboardLayout `panel`). */
const WhatsNewPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const { t } = useTranslation('release-notes')
    const { notes, readIds, unreadCount, markRead, markAllRead } =
        useReleaseNotes()
    const [filter, setFilter] = useState<'all' | 'unread'>('all')
    const [expandedId, setExpandedId] = useState<string | null>(null)

    const visible =
        filter === 'unread'
            ? notes.filter((n) => !readIds.includes(n.id))
            : notes

    const toggle = (id: string) => {
        markRead(id)
        setExpandedId((prev) => (prev === id ? null : id))
    }

    return (
        <AppPanel width={SIDE_PANEL_WIDTH} aria-label={t('whats-new.title')}>
            <AppPanelHeader
                className="!h-[var(--vn-chrome-row-height)]"
                actions={
                    <IconButton
                        aria-label={t('panel.close')}
                        size="sm"
                        onClick={onClose}
                    >
                        <X size={16} />
                    </IconButton>
                }
            >
                <Gift size={18} className="shrink-0 text-primary" />
                <Text weight="semibold" truncate>
                    {t('whats-new.title')}
                </Text>
            </AppPanelHeader>
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
                <SegmentedControl
                    size="sm"
                    aria-label={t('whats-new.title')}
                    value={filter}
                    onValueChange={(v) => setFilter(v as 'all' | 'unread')}
                    items={[
                        {
                            value: 'all',
                            label: `${t('all')} (${notes.length})`,
                        },
                        {
                            value: 'unread',
                            label: `${t('panel.unread')} (${unreadCount})`,
                        },
                    ]}
                />
                <Button
                    size="sm"
                    variant="ghost"
                    className="shrink-0"
                    disabled={unreadCount === 0}
                    onClick={markAllRead}
                >
                    {t('panel.mark-all-read')}
                </Button>
            </div>
            <AppPanelBody className="p-0">
                {visible.length === 0 ? (
                    <EmptyState
                        size="sm"
                        title={
                            filter === 'unread'
                                ? t('empty.no-unread')
                                : t('empty.title')
                        }
                    />
                ) : (
                    visible.map((note) => {
                        const isRead = readIds.includes(note.id)
                        const isExpanded = expandedId === note.id
                        return (
                            <div
                                key={note.id}
                                className="border-b border-border last:border-b-0"
                            >
                                <Disclosure
                                    className="[&>button]:w-full [&>button]:items-start [&>button]:px-4 [&>button]:py-3 [&>button]:text-left [&>button]:text-foreground hover:[&>button]:bg-subtle"
                                    open={isExpanded}
                                    onOpenChange={() => toggle(note.id)}
                                    summary={
                                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                                            <span className="flex items-center gap-2">
                                                <Text weight="semibold">
                                                    v{note.version}
                                                </Text>
                                                <Badge
                                                    size="sm"
                                                    tone={
                                                        CATEGORY_TONE[
                                                            note.category
                                                        ]
                                                    }
                                                    appearance="outline"
                                                >
                                                    {t(
                                                        `category.${note.category}`
                                                    )}
                                                </Badge>
                                                <Text
                                                    size="xs"
                                                    tone="muted"
                                                    className="ml-auto"
                                                >
                                                    {formatDateWithFormatter(
                                                        note.date,
                                                        'DD/MM/YYYY'
                                                    )}
                                                </Text>
                                                {!isRead && (
                                                    <span
                                                        className="h-2 w-2 shrink-0 rounded-full bg-primary"
                                                        role="img"
                                                        aria-label={t(
                                                            'panel.unread'
                                                        )}
                                                    />
                                                )}
                                            </span>
                                            <Text
                                                as="span"
                                                weight={
                                                    isRead
                                                        ? 'medium'
                                                        : 'semibold'
                                                }
                                            >
                                                {note.title}
                                            </Text>
                                            <Text
                                                as="span"
                                                size="sm"
                                                tone="muted"
                                                className={
                                                    isExpanded
                                                        ? ''
                                                        : 'line-clamp-2'
                                                }
                                            >
                                                {note.summary}
                                            </Text>
                                        </span>
                                    }
                                >
                                    <div
                                        className="border-t border-border-subtle px-4 py-3 text-sm leading-relaxed text-muted-foreground"
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHtml(note.content),
                                        }}
                                    />
                                </Disclosure>
                            </div>
                        )
                    })
                )}
            </AppPanelBody>
            <AppPanelFooter>
                <Link
                    to={paths.releaseNotes}
                    onClick={onClose}
                    className="text-sm text-primary-text hover:underline"
                >
                    {t('view-all')}
                </Link>
            </AppPanelFooter>
        </AppPanel>
    )
}

export default WhatsNewPanel
